import React, { useEffect, useState, useMemo, useContext } from "react";
import { fetchCustomerDetails } from "../../api";
import styles from "./CustomerBookingList.module.css";
import Pagination from "../Pagination/Pagination";
import { SearchContext } from "../../context/SearchContext";

const formatPickupOption = (option) => {
  if (!option) return "N/A";
  switch (option) {
    case "pickup":
      return "Pickup Only";
    case "delivery":
      return "Delivery Only";
    case "pickup/delivery":
      return "Pickup & Delivery";
    case "in-store":
      return "In-Store (No Charge)";
    case "express-pickup":
      return "Express Pickup";
    case "express-delivery":
      return "Express Delivery";
    case "express-pickup/delivery":
      return "Express Pickup & Delivery";
    default:
      return option
        .replace(/[-_/]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

const formatTension = (booking) => {
  if (booking.service_kind !== "string") return null;
  const tt = booking.tension_type;
  if (!tt) return null;
  if (tt === "support") return "Talk with support";
  if (
    Number(booking.is_hybrid) === 1 ||
    booking.is_hybrid === true ||
    booking.is_hybrid === "1" ||
    Boolean(booking.string_name_cross) ||
    Boolean(booking.string_id_cross)
  ) {
    if (tt === "standard") return "Main: Standard | Cross: Standard";
    const unit = booking.tension_unit || "lbs";
    const mainPrestretch = booking.prestretch ? ` (+${booking.prestretch_percent ?? "—"}% Prestretch)` : "";
    const crossPrestretch = booking.prestretch_cross ? ` (+${booking.prestretch_percent_cross ?? "—"}% Prestretch)` : "";
    const mainStr = booking.tension_main != null ? `${booking.tension_main} ${unit}${mainPrestretch}` : "Standard";
    const crossStr = booking.tension_cross != null ? `${booking.tension_cross} ${unit}${crossPrestretch}` : "Standard";
    return `Main: ${mainStr} | Cross: ${crossStr}`;
  }
  if (tt === "standard") return "Standard";
  if (tt === "custom") {
    const unit = booking.tension_unit || "lbs";
    let label = `${booking.tension_main ?? "—"} ${unit} main / ${booking.tension_cross ?? "—"} ${unit} cross`;
    if (booking.prestretch) {
      label += `, Prestretch: ${booking.prestretch_percent ?? "—"}%`;
    }
    return label;
  }
  return "—";
};

const getStatusDetails = (booking) => {
  const status = (booking.stringing_status || booking.booking_status || "pending").toLowerCase();
  if (booking.booking_status === "canceled") {
    return { label: "Canceled", className: styles.canceled };
  }
  switch (status) {
    case "pending":
      return { label: "Pending Dropoff", className: styles.pending };
    case "received":
      return { label: "Racket Received", className: styles.received };
    case "ready":
      return { label: "Strung & Ready", className: styles.ready };
    case "completed":
      return { label: "Completed", className: styles.completedBadge };
    case "canceled":
      return { label: "Canceled", className: styles.canceled };
    default:
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        className: styles.pending,
      };
  }
};

const CustomerBookingList = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const { searchTerm } = useContext(SearchContext);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found.");

      const response = await fetchCustomerDetails(token);
      if (!response || !response.data) {
        throw new Error("Invalid API response");
      }
      const fetchedBookings = response.data.bookings || [];
      setBookings(fetchedBookings);

      if (fetchedBookings.length > 0) {
        sessionStorage.setItem("bookings", JSON.stringify(fetchedBookings));
      } else {
        sessionStorage.removeItem("bookings");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const processedBookings = useMemo(() => {
    let filtered = searchTerm
      ? bookings.filter((booking) => {
          const racket = booking.racket_name?.toLowerCase() || "";
          const service = booking.service_name?.toLowerCase() || "";
          const mainStr = booking.string_name?.toLowerCase() || "";
          const crossStr = booking.string_name_cross?.toLowerCase() || "";
          const location = booking.instore_location?.toLowerCase() || "";
          const status = (booking.stringing_status || booking.booking_status || "").toLowerCase();
          const term = searchTerm.toLowerCase();

          return (
            racket.includes(term) ||
            service.includes(term) ||
            mainStr.includes(term) ||
            crossStr.includes(term) ||
            location.includes(term) ||
            status.includes(term)
          );
        })
      : [...bookings];

    // Sort: pending first, then canceled, then completed last
    return filtered.sort((a, b) => {
      const statusA = (a.booking_status || "").toLowerCase();
      const statusB = (b.booking_status || "").toLowerCase();

      const priority = { pending: 1, canceled: 2, completed: 3 };
      const priorityA = priority[statusA] || 1;
      const priorityB = priority[statusB] || 1;

      return priorityA - priorityB;
    });
  }, [bookings, searchTerm]);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentBookings = processedBookings.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(processedBookings.length / itemsPerPage);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>My Bookings</h2>

      {loading ? (
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner}></div>
        </div>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : currentBookings.length > 0 ? (
        <div className={styles.bookingGrid}>
          {currentBookings.map((booking) => {
            const statusObj = getStatusDetails(booking);
            const isHybrid =
              booking.service_kind === "string" &&
              (Number(booking.is_hybrid) === 1 ||
                booking.is_hybrid === true ||
                booking.is_hybrid === "1" ||
                Boolean(booking.string_name_cross) ||
                Boolean(booking.string_id_cross));

            return (
              <div key={booking.booking_id} className={styles.bookingItem}>
                <div className={styles.bookingHeader}>
                  <div className={styles.headerLeft}>
                    <h3>{booking.racket_name || "Tennis Racket"}</h3>
                    <span className={styles.bookingId}>#{booking.booking_id}</span>
                  </div>

                  <span className={`${styles.status} ${statusObj.className}`}>
                    {statusObj.label}
                  </span>
                </div>

                <div className={styles.bookingDetails}>
                  {isHybrid ? (
                    <div className={styles.hybridContainer}>
                      <div className={styles.hybridRow}>
                        <span className={styles.hybridTypeBadge}>Main (Vertical)</span>
                        <span className={styles.hybridStringInfo}>
                          <strong>{booking.string_name || "Bring Own String"}</strong>
                          {booking.string_brand && <span className={styles.hybridBrand}> ({booking.string_brand})</span>}
                        </span>
                        <span className={styles.hybridTensionInfo}>
                          Tension: <strong>{booking.tension_main != null
                            ? `${booking.tension_main} ${booking.tension_unit || "lbs"}${booking.prestretch ? ` (+${booking.prestretch_percent ?? "—"}% Prestretch)` : ""}`
                            : (booking.tension_type === "support" ? "Support" : "Standard")}</strong>
                        </span>
                      </div>
                      <div className={styles.hybridRow}>
                        <span className={styles.hybridTypeBadge}>Cross (Horizontal)</span>
                        <span className={styles.hybridStringInfo}>
                          <strong>{booking.string_name_cross || "Bring Own String"}</strong>
                          {booking.string_brand_cross && <span className={styles.hybridBrand}> ({booking.string_brand_cross})</span>}
                        </span>
                        <span className={styles.hybridTensionInfo}>
                          Tension: <strong>{booking.tension_cross != null
                            ? `${booking.tension_cross} ${booking.tension_unit || "lbs"}${booking.prestretch_cross ? ` (+${booking.prestretch_percent_cross ?? "—"}% Prestretch)` : ""}`
                            : (booking.tension_type === "support" ? "Support" : "Standard")}</strong>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p>
                        <strong>Service:</strong> {booking.service_name || "N/A"}{" "}
                        <span className={styles.serviceTag}>({booking.service_kind})</span>
                      </p>
                      {booking.service_kind === "string" && formatTension(booking) && (
                        <p>
                          <strong>Tension:</strong>{" "}
                          <span style={{ color: "#0b7b43", fontWeight: "600" }}>
                            {formatTension(booking)}
                          </span>
                        </p>
                      )}
                    </>
                  )}

                  <p>
                    <strong>Service Speed:</strong>{" "}
                    <span className={booking.service_type === "Express" ? styles.expressBadge : styles.speedTag}>
                      {booking.service_type || "Regular"}
                    </span>
                  </p>

                  <p>
                    <strong>Pickup Option:</strong> {formatPickupOption(booking.pickup_option)}
                    {booking.pickup_option === "in-store" && booking.instore_location && (
                      <span className={styles.instoreLocation}> @ {booking.instore_location}</span>
                    )}
                  </p>

                  <p>
                    <strong>Booking Date:</strong>{" "}
                    {booking.booking_date
                      ? new Date(booking.booking_date).toLocaleDateString("en-GB")
                      : "N/A"}
                  </p>

                {/* Total Cost + Pickup/Delivery Fee (once per cart) */}
                {(() => {
                  const baseTotal = Number(booking.total_cost || 0);
                  const pickupFeeRow = Number(booking.pickup_fee || 0);

                  const isGrip = booking.service_kind === "grip";
                  const isString = booking.service_kind === "string";

                  // Per-booking amount is always just this row's total_cost
                  const perBookingTotal = baseTotal;

                  let showCartPickupFee = false;
                  let cartPickupFee = 0;

                  if (
                    booking.cart_id &&
                    pickupFeeRow > 0 &&
                    (isGrip || isString)
                  ) {
                    const sameCartSameKind = bookings.filter(
                      (b) =>
                        b.cart_id === booking.cart_id &&
                        b.service_kind === booking.service_kind
                    );

                    if (sameCartSameKind.length > 0) {
                      const first = sameCartSameKind[0];
                      const firstId = first.booking_id;
                      const currentId = booking.booking_id;

                      if (firstId === currentId) {
                        showCartPickupFee = true;

                        if (isGrip) {
                          cartPickupFee = pickupFeeRow;
                        } else if (isString) {
                          cartPickupFee = sameCartSameKind.reduce(
                            (sum, b) => sum + Number(b.pickup_fee || 0),
                            0
                          );
                        }
                      }
                    }
                  }

                  return (
                    <>
                      <p>
                        <strong>Total Cost (Per Booking):</strong>{" "}
                        <span className="dirham-symbol">&#xea;</span>{" "}
                        {perBookingTotal.toFixed(2)}
                      </p>

                      {showCartPickupFee && (
                        <p>
                          <strong>Pickup/Delivery Fee (Cart):</strong>{" "}
                          <span className="dirham-symbol">&#xea;</span>{" "}
                          {cartPickupFee.toFixed(2)}
                        </p>
                      )}
                    </>
                  );
                })()}

                  <p className={styles.paymentStatus}>
                    <strong>Payment Status:</strong>{" "}
                    {booking.payment_option === "online" ? (
                      <span className={styles.paid}>Paid</span>
                    ) : (
                      <span className={styles.unpaid}>
                        <strong>Customer will pay in store</strong>
                      </span>
                    )}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={styles.message}>You have no bookings yet.</p>
      )}

      {processedBookings.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

export default CustomerBookingList;
